// TimerProvider.js
import React from "react";
import { TimerContext, TimerContextValue } from "./TimerContext";
import { useAppContext } from './AppProvider';
import { useIonAlert, useIonRouter } from "@ionic/react";
import { useNotificationSound } from "./components/NotificationSound";

type TimerProviderProps = {
  children: React.ReactNode;
};

export const TimerProvider: React.FC<TimerProviderProps> = ({ children }) => {

  const { authKey, authorizedMember, currentTimer, setCurrentTimer, currentTimerEnd, setCurrentTimerEnd, currentTimerStatus, setCurrentTimerStatus, currentTimers, setCurrentTimers, getCurrentTimers, Preferences } = useAppContext();

  const [showAlert] = useIonAlert();
  const router = useIonRouter();
  const { playNotificationSound } = useNotificationSound();

  // who’s listening
  const listenersRef = React.useRef<
    Set<(secondsLeft: number) => void>
  >(new Set());

  // current interval id (so we can clear it)
  const intervalIdRef = React.useRef<number | null>(null);


  // current end time in ms — THIS is the thing we can change when user edits timer
  const endTimeRef = React.useRef<number | null>(null);

  const currentTimerRef = React.useRef(currentTimer);
  React.useEffect(() => {
    currentTimerRef.current = currentTimer;
  }, [currentTimer]);


  const goToTimer = async (token:string) => {
    //window.location.href = `/account/timer/view/${token}`;
    router.push(`/account/timer/view/${token}`, 'forward');
  };


  // tick function, shared by start + update
  const tick = React.useCallback(() => {
    if (endTimeRef.current == null) return;

    const msLeft = endTimeRef.current - Date.now();
    const secondsLeft = Math.ceil(msLeft / 1000);

    // notify subs
    listenersRef.current.forEach((fn) => fn(secondsLeft));

    //console.log(secondsLeft);

    // Timer Expired Logic
    if (secondsLeft === -1) {
      // stop interval
      /*if (intervalIdRef.current !== null) {
        window.clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }*/
      
      setCurrentTimerStatus('3');

      playNotificationSound();
      
      showAlert({
            header: 'IT\'S CHECK-IN TIME',
            message: 'Go to your Timer to check in or extend the time.',
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    cssClass: 'alert-button-confirm'
                }
            ]                          
      });

    }
  }, []);


  const startIntervalIfNeeded = React.useCallback(() => {
    if (intervalIdRef.current != null) return; // already running
    intervalIdRef.current = window.setInterval(() => {
      tick();
    }, 1000);
  }, [tick]);


  const startCountdown = React.useCallback(
    (endsAtMs?: number) => {
      const fallbackDurationSec = currentTimer?.defaultDurationSec ?? 300;
      const end = endsAtMs ?? Date.now() + fallbackDurationSec * 1000;
      
      // set the ref to the new end time
      endTimeRef.current = end;

      // run an immediate tick so UI updates instantly
      tick();

      // make sure interval is running
      startIntervalIfNeeded();

      //console.log('countdown started');
    },
    [currentTimer, tick, startIntervalIfNeeded]
  );

  // 👇 this is what you call when user edits the check-in time
  const updateCountdownEnd = React.useCallback(
    (endsAtMs: number) => {
      endTimeRef.current = endsAtMs;
      // do an immediate tick so subscribers see the new time right away
      tick();
      // make sure the interval is running (in case it had already stopped)
      startIntervalIfNeeded();
    },
    [tick, startIntervalIfNeeded]
  );

  const stopCountdown = React.useCallback(() => {
    if (intervalIdRef.current !== null) {
      window.clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    endTimeRef.current = null;
    // notify subs that it’s gone (optional)
    listenersRef.current.forEach((fn) => fn(0));
    //console.log('countdown stopped');
  }, []);

  const subscribe = React.useCallback(
    (fn: (secondsLeft: number) => void) => {
      listenersRef.current.add(fn);
      return () => {
        listenersRef.current.delete(fn);
      };
    },
    []
  );

  const value = React.useMemo<TimerContextValue>(
    () => ({
      startCountdown,
      updateCountdownEnd,
      stopCountdown,
      subscribe,
    }),
    [startCountdown, updateCountdownEnd, stopCountdown, subscribe]
  );

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
};
